export type NetworkQualityStatus = "perfect" | "good" | "normal" | "bad"

export interface NetworkQualityState {
  delay: number
  status: NetworkQualityStatus
  reason: string
}

const initialState: NetworkQualityState = {
  delay: 0,
  status: "perfect",
  reason: "当前网络质量优秀",
}

const statusRank: Record<NetworkQualityStatus, number> = {
  perfect: 0,
  good: 1,
  normal: 2,
  bad: 3,
}

/**
 * 根据近 30 秒延迟、抖动和丢包率评估 WebRTC 连接质量。
 * 首批采样仅用于预热，避免刚建连时的统计波动造成误报。
 */
export class NetworkQualityMonitor {
  private delays: number[] = []
  private jitters: number[] = []
  private lostRates: number[] = []
  private warmupSamples = 15
  private wasPoorLatency = false
  private readonly statusReasons = new Map<NetworkQualityStatus, string>()

  reset(): NetworkQualityState {
    this.delays = []
    this.jitters = []
    this.lostRates = []
    this.warmupSamples = 15
    this.wasPoorLatency = false
    this.statusReasons.clear()
    return { ...initialState }
  }

  update(delay: number, lostRate: number): NetworkQualityState {
    const normalizedDelay = Number.isFinite(delay) ? Math.trunc(delay) : 0
    const normalizedLostRate = Number.isFinite(lostRate) ? lostRate : 0
    const status = this.getStatus(normalizedDelay, normalizedLostRate)
    return {
      delay: normalizedDelay,
      status,
      reason: this.statusReasons.get(status) ?? initialState.reason,
    }
  }

  private getStatus(delay: number, lostRate: number): NetworkQualityStatus {
    if (this.warmupSamples > 0) {
      this.warmupSamples -= 1
      return "good"
    }

    const previousDelay = this.delays.at(-1)
    if (previousDelay !== undefined) {
      this.jitters.push(Math.abs(previousDelay - delay))
      if (this.jitters.length > 99) this.jitters.shift()
    }
    this.delays.push(delay)
    if (this.delays.length > 100) this.delays.shift()

    return this.worstStatus(
      this.getAverageDelayStatus(),
      this.getJitterStatus(),
      this.getLostRateStatus(lostRate)
    )
  }

  private getAverageDelayStatus(): NetworkQualityStatus {
    if (this.delays.length === 0) return "perfect"
    const recentDelays = this.delays.slice(-30)
    const average =
      recentDelays.reduce((sum, value) => sum + value, 0) / recentDelays.length
    const tolerance = this.wasPoorLatency ? 5 : 0
    let status: NetworkQualityStatus
    if (average <= 20) status = "perfect"
    else if (average <= 40 - tolerance) status = "good"
    else if (average <= 60 - tolerance) status = "normal"
    else status = "bad"

    this.wasPoorLatency = statusRank[status] > statusRank.good
    if (status !== "perfect") {
      this.statusReasons.set(status, `当前30秒平均延迟${average.toFixed(0)}ms`)
    }
    return status
  }

  private getJitterStatus(): NetworkQualityStatus {
    let count = 0
    let ignoreCount = 0
    for (const jitter of this.jitters) {
      if (ignoreCount > 0) {
        ignoreCount -= 1
        continue
      }
      if (jitter < 50) continue
      count += 1
      ignoreCount = 3
      if (count >= 4) {
        this.statusReasons.set(
          "bad",
          `当前100秒内网络抖动次数为${count}次或以上`
        )
        return "bad"
      }
    }
    if (count === 0) return "perfect"
    const status: NetworkQualityStatus = count < 3 ? "good" : "normal"
    this.statusReasons.set(status, `当前100秒内网络抖动次数为${count}`)
    return status
  }

  private getLostRateStatus(lostRate: number): NetworkQualityStatus {
    this.lostRates.push(lostRate)
    if (this.lostRates.length > 60) this.lostRates.shift()
    const recentLosses = this.lostRates.slice(-4).filter((rate) => rate > 1)
    if (recentLosses.length < 3) return "perfect"
    const peak = recentLosses.at(-1) ?? 0
    const status: NetworkQualityStatus = peak >= 5 ? "bad" : "normal"
    this.statusReasons.set(status, `当前网络最高丢包率为${peak.toFixed(2)}%`)
    return status
  }

  private worstStatus(
    ...statuses: NetworkQualityStatus[]
  ): NetworkQualityStatus {
    return statuses.reduce((worst, status) =>
      statusRank[status] > statusRank[worst] ? status : worst
    )
  }
}
